import React, { useMemo, useState } from "react";
import ConfirmationModal from "../../../shared/ConfirmationModal";
import TermsAndConditionsModal from "./TermsAndConditionsModal";
import TermsAndConditionsTable from "./TermsAndConditionsTable";
import { TripConfigApplicableFor } from "../../TripEventConsts";

const termTypes = {
  long: "LongText",
  short: "ShortText",
};

export default function EventTermsAndConditions({ event, onChange }) {
  const [currentTermsIndex, setCurrentTermsIndex] = useState(null);
  const [newTermsAppliesTo, setNewTermsAppliesTo] = useState(null);
  const [
    showDeleteTermsConfirmationModal,
    setShowDeleteTermsConfirmationModal,
  ] = useState(false);
  const [showTermsAndConditionsModal, setShowTermsAndConditionsModal] =
    useState(false);

  const onAddTerms = (appliesTo) => {
    setCurrentTermsIndex(null);
    setNewTermsAppliesTo(appliesTo);
    setShowTermsAndConditionsModal(true);
  };

  const onDeleteTerms = (termsIndex) => {
    setCurrentTermsIndex(termsIndex);
    setShowDeleteTermsConfirmationModal(true);
  };

  const onUpdateTerms = (termsIndex) => {
    setCurrentTermsIndex(termsIndex);
    setShowTermsAndConditionsModal(true);
  };

  const addTerms = (newTerms) => {
    const newTermsAndConditions = [
      ...event.termsAndConditions,
      {
        ...newTerms,
        appliesTo: newTermsAppliesTo,
      },
    ];
    onChange("termsAndConditions", newTermsAndConditions);

    setCurrentTermsIndex(null);
    setNewTermsAppliesTo(null);
    setShowTermsAndConditionsModal(false);
  };

  const deleteTerms = () => {
    const newTermsAndConditions = [...event.termsAndConditions];
    newTermsAndConditions.splice(currentTermsIndex!, 1);
    onChange("termsAndConditions", newTermsAndConditions);

    setCurrentTermsIndex(null);
    setShowDeleteTermsConfirmationModal(false);
  };

  const updateTerms = (updatedTerms) => {
    const newTermsAndConditions = [...event.termsAndConditions];
    newTermsAndConditions[currentTermsIndex!] = updatedTerms;
    onChange("termsAndConditions", newTermsAndConditions);

    setCurrentTermsIndex(null);
    setShowTermsAndConditionsModal(false);
  };

  const { allowAdditionalChaperones, isTravelTrip, termsAndConditions } = event;

  const termsAndConditionsWithIndexes = termsAndConditions
    ? termsAndConditions.map((terms, index) => ({
        termsIndex: index,
        terms,
      }))
    : [];

  const termsAndConditionsTypes = useMemo(
    () =>
      [
        { type: TripConfigApplicableFor.Shluchim, condition: !isTravelTrip },
        { type: TripConfigApplicableFor.Students, condition: true },
        {
          type: TripConfigApplicableFor.Chaperones,
          condition: allowAdditionalChaperones,
        },
      ]
        .filter(({ condition }) => condition)
        .map(({ type }) => type),
    [allowAdditionalChaperones, isTravelTrip]
  );

  return (
    <React.Fragment>
      <div className="program-settings-page-section">
        <p className="program-settings-page-title xxl-text fw-500">
          Terms and Conditions
        </p>
      </div>
      {termsAndConditionsTypes.map((type) => (
        <TermsAndConditionsTable
          key={type}
          onAddTerms={() => onAddTerms(type)}
          onDeleteTerms={onDeleteTerms}
          onUpdateTerms={onUpdateTerms}
          termsAndConditions={termsAndConditionsWithIndexes.filter(
            ({ terms }) => terms.appliesTo === type
          )}
          termTypes={termTypes}
          title={type}
        />
      ))}
      <TermsAndConditionsModal
        close={() => setShowTermsAndConditionsModal(false)}
        save={currentTermsIndex === null ? addTerms : updateTerms}
        show={showTermsAndConditionsModal}
        terms={
          currentTermsIndex === null
            ? null
            : termsAndConditions[currentTermsIndex]
        }
        termTypes={termTypes}
      />

      <ConfirmationModal
        cancel={() => setShowDeleteTermsConfirmationModal(false)}
        confirm={deleteTerms}
        message="Are you sure you want to delete these terms and conditions?"
        show={showDeleteTermsConfirmationModal}
      />
    </React.Fragment>
  );
}
